/**
 * BatchGeoHeatMap is a super tiny (<50 lines without comments)
 * component that swaps out the default map markers for a google maps heat
 * visualization, showcasing the density of markers.
 */
(function (window) {
  window.BatchGeoHeatMap = function () {
    var self = this;
    var map = window.batchGeoGoogleMapsManager.getMap();
    var heatMapPersist = new BatchGeoPersistence({ key: "HeatMap" });
    var mapId = BatchGeoStore.getState().App.mapId;

    var _validateNumber = function (data) {
      if (!data) {
        return false;
      }

      if (typeof data == "number") {
        return data;
      }

      // Use a regular expression to match only the digits in the string
      const match = data.match(/\d+/g);

      // Return the matched digits as a number
      if (match) {
        return parseInt(match.join(""));
      } else {
        return 0;
      }
    };

    var _composeHeatmapData = function (per, columnToAggregate) {
      const columnData = [];
      // prepare data and weight scaling
      let numberOfLoops = per.mapRS.length;
      for (let i = 0; i < numberOfLoops; i++) {
        const currentMarker = per.mapRS[i];
        const columnValue = _validateNumber(per.dataRS[i][columnToAggregate]);

        // skip if invalid
        if (!columnValue) {
          continue;
        }

        //format data
        const formattedData = {
          location: new google.maps.LatLng(currentMarker.lt, currentMarker.ln),
          weight: columnValue,
        };

        columnData.push(formattedData);
      }

      return columnData;
    };

    // https://en.wikipedia.org/wiki/Feature_scaling
    var _scaleToRange = function (
      data,
      targetKey = "weight",
      range = { min: 1, max: 10 },
    ) {
      const values = data.map((item) => item[targetKey]);
      const maxValue = Math.max(...values);
      const minValue = Math.min(...values);
      const scaledData = data.map((item) => {
        item[targetKey] =
          ((item[targetKey] - minValue) * (range.max - range.min)) /
            (maxValue - minValue) +
          range.min;
        return item;
      });
      return scaledData;
    };

    /**
     * _getPoints(): Maps our marker data to an array of just location data to
     * be fed into the heatmap layer.
     *
     * @return {array} marker location data
     */
    var _getPoints = function () {
      if (
        window.per.clustering_icon_mode_sel &&
        window.per.clustering_icon_mode_sel == "sum" &&
        window.per.clustering_icon_col > -1
      ) {
        var columnToAggregate = window.per.clustering_icon_col;
        // per contains mapRS and dataRS, which contain marker positions and corresponding column datas.
        var per = window.batchGeoGoogleMapsMarker.options.markersData;
        const heatmapData = _composeHeatmapData(per, columnToAggregate);
        const scaledheatmapData = _scaleToRange(heatmapData);
        return scaledheatmapData;
      } else {
        var markers = window.batchGeoGoogleMapsMarker.getMarkersObjArray();
        // First filter our undefined/null markers
        return markers
          .filter(function (marker) {
            return marker;
            // Now make a new array with the positions
          })
          .map(function (marker) {
            return marker.marker.position;
          });
      }
    };

    /**
     * _clearMapMarkers(): Removes the individual markers as well as cluster
     * markers from the map.
     */
    var _clearMapMarkers = function () {
      const MarkerDrawer = window.batchGeoGoogleMapsMarker.MarkerDrawer;
      window.batchGeoGoogleMapsMarker.closeMarker();

      // if clustering is on, clear clustering markers
      if (window.per.clustering_cb_status && window.clarker) {
        window.clarker.clearMarkers();
      }

      // clear individual markers
      MarkerDrawer.getMarkers().forEach(function (marker) {
        if (marker) marker.setMap(null);
      });

      // remove labels from dom
      MarkerDrawer.deleteLabels();

      BatchGeoStore.dispatch({
        type: "ROW_SELECT_DESELECTALL",
      });
    };

    /**
     * _heatmapEventHandler holds all of the heatmap events and their callbacks.
     * It also provides methods to initialize or remove everything at once.
     * @type {Object}
     */
    var _heatmapEventHandler = {
      /**
       * Actions is a collection of event data with callbacks and their own methods
       * to initialize and remove.
       * @type {Array}
       */
      actions: [
        /**
         * Action:
         *
         * name {string} (optional) : Name of the action
         * events {string} : Events to listen for
         * callback {function} : Code to be fired on event
         * init: {function} : Method to attach `this.callback` to `this.events`
         * remove {function} : Method to remove `this.callback from `this.events`
         *
         * @type {Object}
         */
        {
          name: "filter",
          events: "batchgeo.filter.update batchgeo.filter.clear",
          callback: function () {
            self.update({ isFilter: true });
          },
          init: function () {
            $(window).on(this.events, this.callback);
          },
          remove: function () {
            $(window).off(this.events, this.callback);
          },
        },
      ],
      /**
       * init(): Loop through our actions and fire the related init() function
       * for each action event listener.
       */
      init: function () {
        this.actions.forEach(function (action) {
          action.init();
        });
      },
      /**
       * remove(): Loop through our actions and remove them.
       */
      remove: function () {
        this.actions.forEach(function (action) {
          action.remove();
        });
      },
    };

    /**
     * enable() simply enables the heatmap layer, and hides the current markers.
     */
    var enable = function () {
      map = window.batchGeoGoogleMapsManager.getMap();
      this.heatmap.setData(_getPoints());

      this.heatmap.setMap(map);

      // clear out map markers since we're turning on a heatmap and don't need both
      _clearMapMarkers();

      // init our heatmap relevent listeners
      _heatmapEventHandler.init();

      // Turn marker drawing off while in heatmap mode
      BatchGeoStore.dispatch({
        type: "MARKER_DRAWER_DISABLE_DRAWING",
      });

      heatMapPersist.set(mapId, true);
      BatchGeoStore.dispatch({ type: "HEATMAP_ENABLE" });
    };

    /**
     * update() tells the heatmap to update to the current global markers
     *
     * @param {object} options: an options hash: currently only one option is necessary
     * or supported, which is options.isFilter, which lets us know when to clear
     * the default markers again since filters try to bring them back.
     */
    var update = function (options) {
      options = options || {};
      // set heatmap with new data
      this.heatmap.setData(_getPoints());
      // filters try and bring back markers so get rid of them
      if (options.isFilter) _clearMapMarkers();
    };

    /**
     * disable() simply sets the heatmap layer to null, and triggers a redraw
     * of the default marker arrangement, as well as removing filter listeners.
     */
    var disable = function () {
      // If it hasn't been initialized just return early
      if (!this.heatmap) return false;
      this.heatmap.setMap(null);

      // remove listeners
      _heatmapEventHandler.remove();

      // Turn marker drawing back on
      BatchGeoStore.dispatch({
        type: "MARKER_DRAWER_ENABLE_DRAWING",
      });

      // drawMarkers handles clustering so don't need to turn that back on
      window.batchGeoGoogleMapsMarker.MarkerDrawer.draw();

      heatMapPersist.set(mapId, false);
      BatchGeoStore.dispatch({ type: "HEATMAP_DISABLE" });
    };

    //consistent radius size for informative heatmap
    const radius = 20;

    //single green -> red heatmap gradient scheme
    const gradient = [
      "rgba(0, 255, 0, 0)",
      "rgba(0, 255, 0, 1)",
      "rgba(63, 255, 0, 1)",
      "rgba(127, 255, 0, 1)",
      "rgba(191, 255, 0, 1)",
      "rgba(255, 255, 0, 1)",
      "rgba(255, 191, 0, 1)",
      "rgba(255, 127, 0, 1)",
      "rgba(255, 63, 0, 1)",
      "rgba(255, 63, 0, 1)",
      "rgba(255, 0, 0, 1)",
      "rgba(255, 0, 0, 1)",
      "rgba(255, 0, 0, 1)",
      "rgba(255, 0, 0, 1)",
      "rgba(255, 0, 0, 1)",
    ];

    this.heatmap = new google.maps.visualization.HeatmapLayer({
      map: map,
      data: [],
      radius: radius,
      gradient: gradient,
      dissipating: true,
      maxIntensity: 10,
    });

    // make some of our methods public
    this.enable = enable;
    this.disable = disable;
    this.update = update;
    // and return them to the outside world
    return this;
  };
})(window);
